import React, { useMemo, JSX } from 'react';
import { observer, useLocalObservable } from 'mobx-react';
import SearchCollapsible from '../../SubModules/SearchCollapsible/SearchCollapsible';
import DateDisplay from '../../Components/UI/DateDisplay/DateDisplay';
import { PackageSearch } from '../../SubModules/PackageSearch/PackageSearch';
import Props from './externals';
import fallback from '../../../_Translations/generated/package.en.t.json';
import Keys from '../../../_Translations/generated/package.en.t.json.keys';
import useTranslation from '../../Hooks/useTranslation';
import PackageStore from './PackageStore';
import ErrorDisplay from './ErrorDisplay';
import PackageSearchStore from '../../SubModules/PackageSearch/PackageSearchStore';
import useAppService from '../../Hooks/useAppService';
import { useTheme } from '@emotion/react';

const RenderSearchContent = ({
  searchParams,
  searchStore,
  renderSearchContent,
  store
}: {
  searchParams: Props['searchParams'];
  searchStore: PackageSearchStore;
  renderSearchContent: Props['renderSearchContent'];
  store: PackageStore;
}): JSX.Element => {
  const { t } = useTranslation('package', fallback, Keys);

  const numberOfChildren = useMemo(
    () => searchParams?.occupancy?.reduce((total, current) => total + current.children, 0),
    [searchParams]
  );

  return (
    <>
      {renderSearchContent ? (
        renderSearchContent(store, searchStore, searchParams)
      ) : (
        <>
          {searchParams?.destination?.name && <div>{searchParams.destination.name}</div>}
          <div>{searchParams?.startDate && <DateDisplay date={searchParams.startDate} />}</div>
          <div>
            {`${t(Keys.adults)}: ${searchParams?.occupancy?.reduce(
              (total, current) => total + current.adults,
              0
            )}`}
            {numberOfChildren ? `, ${t(Keys.children)}: ${numberOfChildren}` : ''}
          </div>
        </>
      )}
    </>
  );
};

const SearchWidget = observer((props: Props & { store: PackageStore }) => {
  const app = useAppService();
  const theme = useTheme();
  const searchStore =
    props.customPackageSearchStore ||
    useLocalObservable(() => new PackageSearchStore(app.config, theme));
  const {
    collapsibleRef,
    searchParams,
    showLabels,
    subType,
    hideOrigin,
    destinationMandatory,
    endDateMandatory,
    withDateRangePicker,
    withAdvancedDateRangePicker,
    withGroupRequestForm,
    renderSearch,
    hideDestination,
    minDateRangeSpan,
    maxDateRangeSpan,
    onSearch,
    withRoomCountSelector,
    store,
    minSelectableChildAge,
    noDatePickerOverflow,
    selectLabelSort,
    renderSearchContent,
    desktopSearchCollapse,
    stickySearch,
    withChildrenSelection,
    changeOnBlur,
    onlyAdultSelect,
    isStickyListener,
    renderDestinationLabel,
    optionsMapper,
    packageOriginMustBeSet,
    packageOriginWithRemoveAll,
    packageOriginWithSelectAll,
    customIndexedDestinationSelect,
    dateRangeDurationProps,
    widgetClickable,
    elementsBeforePaxSelector,
    dateRangeUpdateStateFromProps,
    dateRangeFillWithLastDateRangeWhenNoSelection,
    dateRangeClearSelectionsOnOpen,
    showPaxSelectionInModal,
    showDateSelectionInModal,
    modalLabel,
    paxSelectionValidateChildrenPaxSelectionOnLeave,
    paxSelectionValidateChildrenPaxSelectionOnChange,
    paxSelectionRevalidateChildrenOnChangeAfterError,
    paxSelectionUpdatePaxOnLeaveWhenValid,
    paxSelectionUpdatePaxOnChangeWhenValid,
    paxRoomSelectionImperativeHandleRef,
    onPackageSearchChange,
    childAgePickerHeader,
    useGeoUnitTwoColumnStyling
  } = props;

  const { t } = useTranslation('package', fallback, Keys);

  return (
    <SearchCollapsible
      ref={collapsibleRef}
      collapseLabel={t(Keys.change)}
      desktopSearchCollapse={desktopSearchCollapse}
      stickySearch={stickySearch}
      widgetClickable={widgetClickable}
      isStickyListener={isStickyListener}
      lowerContent={
        stickySearch ? (
          <div>
            <ErrorDisplay store={store} />
          </div>
        ) : (
          <></>
        )
      }
      searchContent={
        <RenderSearchContent
          searchStore={searchStore}
          renderSearchContent={renderSearchContent}
          store={store}
          searchParams={searchParams}
        />
      }
    >
      <PackageSearch
        store={searchStore}
        searchParams={searchParams}
        onSubmit={(params): void => {
          if (onSearch) {
            params.isFromSearchButton = true;
            onSearch(params, store.listOptions);
          }
        }}
        withChildAgePicker={props.serviceSearchWithChildAgePicker}
        childAgeRestriction={props.serviceSearchChildAgeRestriction}
        withRoomCountSelector={withRoomCountSelector}
        showLabels={showLabels}
        subType={subType}
        hideOrigin={hideOrigin}
        destinationMandatory={destinationMandatory}
        endDateMandatory={endDateMandatory}
        withDateRangePicker={withDateRangePicker}
        withAdvancedDateRangePicker={withAdvancedDateRangePicker}
        withGroupRequestForm={withGroupRequestForm}
        renderSearch={renderSearch}
        hideDestination={hideDestination}
        minDateRangeSpan={minDateRangeSpan}
        maxDateRangeSpan={maxDateRangeSpan}
        minSelectableChildAge={minSelectableChildAge}
        noDatePickerOverflow={noDatePickerOverflow}
        selectLabelSort={selectLabelSort}
        withChildrenSelection={withChildrenSelection}
        changeOnBlur={changeOnBlur}
        onlyAdultSelect={onlyAdultSelect}
        renderDestinationLabel={renderDestinationLabel}
        optionsMapper={optionsMapper}
        packageOriginMustBeSet={packageOriginMustBeSet}
        packageOriginWithRemoveAll={packageOriginWithRemoveAll}
        packageOriginWithSelectAll={packageOriginWithSelectAll}
        customIndexedDestinationSelect={customIndexedDestinationSelect}
        dateRangeDurationProps={dateRangeDurationProps}
        elementsBeforePaxSelector={elementsBeforePaxSelector}
        collapsibleRef={collapsibleRef}
        dateRangeUpdateStateFromProps={dateRangeUpdateStateFromProps}
        dateRangeClearSelectionsOnOpen={dateRangeClearSelectionsOnOpen}
        dateRangeFillWithLastDateRangeWhenNoSelection={
          dateRangeFillWithLastDateRangeWhenNoSelection
        }
        showPaxSelectionInModal={showPaxSelectionInModal}
        showDateSelectionInModal={showDateSelectionInModal}
        modalLabel={modalLabel}
        paxSelectionRevalidateChildrenOnChangeAfterError={
          paxSelectionRevalidateChildrenOnChangeAfterError
        }
        paxSelectionValidateChildrenPaxSelectionOnLeave={
          paxSelectionValidateChildrenPaxSelectionOnLeave
        }
        paxSelectionValidateChildrenPaxSelectionOnChange={
          paxSelectionValidateChildrenPaxSelectionOnChange
        }
        paxRoomSelectionImperativeHandleRef={paxRoomSelectionImperativeHandleRef}
        paxSelectionUpdatePaxOnLeaveWhenValid={paxSelectionUpdatePaxOnLeaveWhenValid}
        paxSelectionUpdatePaxOnChangeWhenValid={paxSelectionUpdatePaxOnChangeWhenValid}
        onPackageSearchChange={onPackageSearchChange}
        childAgePickerHeader={childAgePickerHeader}
        useGeoUnitTwoColumnStyling={useGeoUnitTwoColumnStyling}
      />
    </SearchCollapsible>
  );
});

export default SearchWidget;
